'use client'
import * as React from 'react'
import { InputContainer, inputContainerVariants } from './input-container'
import { InputBase } from './input-base'
import { cn } from '../../lib/utils'
import { Icon } from '../icon'
import { Button, type ButtonVariants } from '../button'
import type { VariantProps } from 'class-variance-authority'
import gsap from 'gsap'

export interface InputPasswordProps
  extends React.InputHTMLAttributes<HTMLInputElement>,
    VariantProps<typeof inputContainerVariants> {
  'data-error'?: boolean
}

const InputPassword = React.forwardRef<HTMLInputElement, InputPasswordProps>(
  ({ variant, inputSize, className, ...props }, ref) => {
    const sizeKey = inputSize ?? 'md'
    const [isFocused, setIsFocused] = React.useState(false)
    const wrapperSizeVariants: Record<
      'sm' | 'md' | 'lg',
      ButtonVariants['size']
    > = {
      sm: 'xs',
      md: 'sm',
      lg: 'md',
    }

    const [show, setShow] = React.useState(false)
    const iconRef = React.useRef<HTMLSpanElement>(null)
    const prevShow = React.useRef(show)

    const handleToggle = () => setShow((prev) => !prev)

    React.useEffect(() => {
      if (iconRef.current && prevShow.current !== show) {
        gsap.fromTo(
          iconRef.current,
          { y: show ? 12 : -12, opacity: 0 },
          { y: 0, opacity: 1, duration: 0.25, ease: 'power2.out' },
        )
      }
      prevShow.current = show
    }, [show])

    return (
      <InputContainer
        variant={variant}
        inputSize={inputSize}
        data-error={props['data-error']}
        data-focused={isFocused}
        className={cn(className)}
      >
        <div className='relative w-full flex items-center '>
          <InputBase
            ref={ref}
            {...props}
            type={show ? 'text' : 'password'}
            className={cn(className, 'font-[AzarMehr] ltr pl-xs')}
            onFocus={(e) => {
              setIsFocused(true)
              props.onFocus?.(e as React.FocusEvent<HTMLInputElement>)
            }}
            onBlur={(e) => {
              setIsFocused(false)
              props.onBlur?.(e as React.FocusEvent<HTMLInputElement>)
            }}
          />
          <Button
            variant='transparent'
            color='neutral'
            type='button'
            onClick={handleToggle}
            aria-label={show ? 'Hide password' : 'Show password'}
            size={wrapperSizeVariants[sizeKey]}
            startIcon={
              <span ref={iconRef} className='inline-flex'>
                {show ? (
                  <Icon name='EyeOpenedLine' size='parent' />
                ) : (
                  <Icon name='EyeClosedLine' size='parent' />
                )}
              </span>
            }
          />
        </div>
      </InputContainer>
    )
  },
)

InputPassword.displayName = 'InputPassword'

export { InputPassword }
